import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ErrorService } from 'src/app/services/error.service';
import { PatientsService } from 'src/app/services/patients.service';
import { VisitsService } from 'src/app/services/visits.service';
import { AuditLabel } from 'src/app/types/patient.types';
import { clientErrored } from '../error/error.actions';
import * as patientGraphActions from '../patient-graph/patient-graph.actions';
import { currentVisitPayload, patientId } from '../patient-graph/patient-graph.selectors';
import * as patientActions from '../patient/patient.actions';
import { AppState } from '../reducers';
import * as patientAuditActions from './patient-audit.actions';
import { PatientAuditQuery, PatientAuditQueryType } from './patient-audit.actions';
import { loadedOnce, query } from './patient-audit.selectors';

@Injectable()
export class PatientAuditEffects {

	constructor(
		private store: Store<AppState>, private actions$: Actions,
		private visitsService: VisitsService, private patientService: PatientsService, private errorService: ErrorService) {
	}

	patientGraphLoaded$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientGraphActions.patientGraphLoaded),
			concatLatestFrom(() => this.store.select(currentVisitPayload)),
			map(([, currentVisitPayload]) => {
				var query: Partial<PatientAuditQuery> = {};

				if (currentVisitPayload.visit) {
					query.queryType = patientAuditActions.PatientAuditQueryType.Visit;
					query.visitId = currentVisitPayload.visit.id;
				}
				else {
					query.queryType = patientAuditActions.PatientAuditQueryType.Patient;
				}

				return patientAuditActions.queryAndFiltersInitialised({ query });
			})
		)
	);

	segmentChanged$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientActions.segmentChanged),
			concatLatestFrom(() => this.store.select(loadedOnce)),
			filter(([action, loadedOnce]) => action.label === AuditLabel && !loadedOnce),
			map(() => patientAuditActions.queryChanged({ query: {} }))
		)
	);

	patientQueryChanged$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientAuditActions.queryChanged),
			concatLatestFrom(() => this.store.select(query)),
			filter(([, query]) => query.queryType === PatientAuditQueryType.Patient),
			map(([, query]) => query),
			concatLatestFrom(() => [this.store.select(patientId)]),
			switchMap(([query, patientId]) => this.patientService.getAuditLog(patientId, query.maxResults).pipe(
				map(auditLog => patientAuditActions.auditLogChanged({ auditLog })),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Get patient audit failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);

	visitQueryChanged$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientAuditActions.queryChanged),
			concatLatestFrom(() => this.store.select(query)),
			filter(([, query]) => query.queryType === PatientAuditQueryType.Visit),
			map(([, query]) => query),
			switchMap((query) => this.visitsService.getAuditLog(query.visitId!, query.maxResults).pipe(
				map(auditLog => patientAuditActions.auditLogChanged({ auditLog })),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Get visit audit failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);
}